<template>
  <div
    class="slm"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Components.SponsorsListModal.allSponsors')"
  >
    <!-- Header -->
    <div class="slm__header">
      <div class="slm__title-block">
        <h2 class="slm__title">
          {{ $t('Components.SponsorsListModal.sponsors') }}
        </h2>
        <p class="slm__sub">{{ totalLabel }}</p>
      </div>
      <button
        class="slm__close"
        @click="onClose"
        :aria-label="$t('Components.SponsorsListModal.close')"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Search + category filter — shown only when the admin enabled the
         "Filters" toggle for sponsors (otherwise hidden entirely). -->
    <div v-if="showFilters" class="slm__filters">
      <div class="slm__search-wrap">
        <i class="fas fa-search slm__search-icon" aria-hidden="true"></i>
        <input
          v-model="searchInput"
          type="text"
          class="slm__search"
          :placeholder="$t('Components.SponsorsListModal.searchPlaceholder')"
          :aria-label="$t('Components.SponsorsListModal.searchSponsors')"
        />
      </div>

      <div v-if="filterOptions.length" class="slm__category-wrap">
        <select
          v-model="selectedCategoryId"
          class="slm__category"
          :aria-label="$t('Components.SponsorsListModal.filterByCategory')"
        >
          <option :value="null">
            {{ $t('Components.SponsorsListModal.allCategories') }}
          </option>
          <option v-for="cat in filterOptions" :key="cat.key" :value="cat.key">
            {{ cat.label }}
          </option>
        </select>
        <i
          class="fas fa-chevron-down slm__category-chevron"
          aria-hidden="true"
        ></i>
      </div>
    </div>

    <!-- Vertical list -->
    <div class="slm__list">
      <div
        v-for="sponsor in filteredSponsors"
        :key="sponsor.id || sponsor.Id"
        class="slm-row"
        role="button"
        tabindex="0"
        @click="openSponsor(sponsor)"
        @keypress.enter="openSponsor(sponsor)"
      >
        <div class="slm-row__logo-wrap">
          <img
            v-if="sponsor.logoUrl"
            :src="sponsor.logoUrl"
            :alt="
              sponsor.name ||
              sponsor.companyName ||
              $t('Components.SponsorsListModal.sponsor')
            "
            class="slm-row__logo"
          />
          <span v-else class="slm-row__logo-fallback" aria-hidden="true">
            <i class="far fa-building"></i>
          </span>
        </div>

        <div class="slm-row__info">
          <div class="slm-row__name">
            {{
              sponsor.name ||
              sponsor.companyName ||
              $t('Components.SponsorsListModal.sponsor')
            }}
          </div>
          <div v-if="sponsorFilterLabels(sponsor)" class="slm-row__category">
            {{ sponsorFilterLabels(sponsor) }}
          </div>
        </div>

        <i class="fas fa-chevron-right slm-row__chevron" aria-hidden="true"></i>
      </div>

      <div v-if="!filteredSponsors.length" class="slm-empty">
        <i
          class="far fa-building"
          style="font-size: 30px; color: #d1d5db; margin-bottom: 8px"
        ></i>
        <p>{{ $t('Components.SponsorsListModal.noResults') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SponsorInfoModal from '@/components/SponsorInfoModal';
import focusTrapMixin from '@/mixins/focusTrap';
import sponsorCategories from '@/mixins/sponsorCategories';

export default {
  name: 'SponsorsListModal',
  mixins: [focusTrapMixin, sponsorCategories],
  props: {
    // Whether to show the search + category filter bar. Driven by the
    // admin's sponsor "Filters" toggle; defaults to true so other
    // callers keep their existing behaviour.
    showFilters: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchInput: '',
      selectedCategoryId: null
    };
  },
  computed: {
    ...mapGetters('Sponsors', { allSponsors: 'getItems' }),
    ...mapGetters('Categories', {
      sponsorsCategories: 'getSponsorsCategories',
      allCategoriesRaw: 'getItems'
    }),
    allCategories() {
      const raw = this.allCategoriesRaw;
      if (Array.isArray(raw)) return raw;
      if (raw && typeof raw === 'object') return Object.values(raw);
      return [];
    },
    sortedSponsors() {
      const list = Array.isArray(this.allSponsors)
        ? this.allSponsors
        : Object.values(this.allSponsors || {});
      return list.slice().sort((a, b) => {
        const oa = parseInt(a && a.order) || 0;
        const ob = parseInt(b && b.order) || 0;
        return oa - ob;
      });
    },
    // Lookup from category id (any case) to normalized label key.
    categoryIdToKey() {
      const map = new Map();
      const seedFrom = (list) => {
        if (!Array.isArray(list)) return;
        list.forEach((c) => {
          if (!c) return;
          const id = this.pickCategoryId(c);
          const label = this.pickCategoryLabel(c);
          if (id && label) {
            map.set(String(id), String(label).trim().toLowerCase());
          }
        });
      };
      // Seed from ALL categories in the store — admin-defined types
      // (SPONSOR_CATEGRORY=3 or others) may fall outside the
      // filtered getter but still be referenced by sponsors via id.
      seedFrom(this.allCategories);
      seedFrom(this.sponsorsCategories);
      return map;
    },
    // { key, label } options where key is the normalized lowercase
    // label — same normalization sponsorCategoryKeys() applies, so
    // the dropdown value and the sponsor's tag value always match
    // regardless of which field carries the category identifier.
    sponsorCategoryOptions() {
      const seen = new Map();
      const add = (c) => {
        if (c === null || c === undefined) return;
        let label = '';
        if (typeof c === 'string' || typeof c === 'number') {
          const str = String(c);
          const looked = this.categoryIdToKey.get(str);
          if (looked) {
            const match = (this.allCategories || []).find(
              (cat) => String(this.pickCategoryId(cat)) === str
            );
            label = match ? this.pickCategoryLabel(match) : str;
          } else {
            label = str;
          }
        } else {
          label = this.pickCategoryLabel(c);
          if (!label) {
            const id = this.pickCategoryId(c);
            const looked = this.categoryIdToKey.get(String(id));
            if (looked) {
              const match = (this.sponsorsCategories || []).find(
                (cat) => String(this.pickCategoryId(cat)) === String(id)
              );
              label = match ? this.pickCategoryLabel(match) : String(id);
            } else {
              label = String(id);
            }
          }
        }
        if (!label) return;
        const key = String(label).trim().toLowerCase();
        if (!key) return;
        if (!seen.has(key)) seen.set(key, { key, label: String(label) });
      };
      if (Array.isArray(this.sponsorsCategories)) {
        this.sponsorsCategories.forEach(add);
      }
      this.sortedSponsors.forEach((s) => {
        this.pickEntityCategories(s).forEach(add);
      });
      return Array.from(seen.values());
    },
    // "All categories" dropdown options — only the FILTER categories
    // (e.g. VIP), not the sponsor types.
    filterOptions() {
      return this.sponsorFilterOptions(this.sortedSponsors);
    },
    filteredSponsors() {
      const q = (this.searchInput || '').trim().toLowerCase();
      const catKey = this.selectedCategoryId
        ? String(this.selectedCategoryId).trim().toLowerCase()
        : null;
      return this.sortedSponsors.filter((s) => {
        if (catKey && !this.sponsorFilterKeys(s).includes(catKey)) {
          return false;
        }
        if (!q) return true;
        const haystack = [
          s.name || '',
          s.companyName || '',
          s.shortDescription || ''
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    },
    totalLabel() {
      const n = this.sortedSponsors.length;
      if (!n) return this.$t('Components.SponsorsListModal.noSponsors');
      return this.$tc('Components.SponsorsListModal.sponsorCount', n, { n });
    }
  },
  created() {
    document.body.classList.add('overflow-hidden');
    this.fetchSponsors();
    this.fetchCategories();
  },
  mounted() {
    this.activateFocusTrap();
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
    document.body.classList.remove('overflow-hidden');
  },
  methods: {
    ...mapActions('Sponsors', { fetchSponsors: 'fetchItems' }),
    ...mapActions('Categories', { fetchCategories: 'fetchItems' }),
    pickCategoryLabel(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'label',
        'Label',
        'value',
        'Value',
        'name',
        'Name',
        'title',
        'Title',
        'caption',
        'Caption',
        'text',
        'Text',
        'categoryName',
        'CategoryName',
        'displayName',
        'DisplayName'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    pickCategoryId(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      const keys = [
        'id',
        'Id',
        'ID',
        'categoryId',
        'CategoryId',
        'categoryID',
        'CategoryID',
        'cid',
        'Cid'
      ];
      for (const k of keys) {
        if (c[k] !== undefined && c[k] !== null && String(c[k]).trim() !== '') {
          return String(c[k]);
        }
      }
      return '';
    },
    pickEntityCategories(entity) {
      if (!entity) return [];
      const keys = [
        'sponsorCategories',
        'SponsorCategories',
        'speakerCategories',
        'SpeakerCategories',
        'sponsorCategoryIds',
        'SponsorCategoryIds',
        'speakerCategoryIds',
        'SpeakerCategoryIds',
        'categoryIds',
        'CategoryIds',
        'categories',
        'Categories',
        'category',
        'Category',
        'categoryId',
        'CategoryId'
      ];
      for (const k of keys) {
        const v = entity[k];
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) return v;
        return [v];
      }
      return [];
    },
    // Normalized category keys used both for the filter dropdown
    // and the row-level "categories" hint.
    sponsorCategoryKeys(sponsor) {
      if (!sponsor) return [];
      const raw = this.pickEntityCategories(sponsor);
      if (!raw.length) return [];
      const lookup = this.categoryIdToKey;
      return raw
        .map((c) => {
          if (c === null || c === undefined) return '';
          const inlineLabel =
            typeof c === 'object' ? this.pickCategoryLabel(c) : '';
          if (inlineLabel && typeof c === 'object') {
            return String(inlineLabel).trim().toLowerCase();
          }
          const id = this.pickCategoryId(c);
          if (!id) {
            const scalar =
              typeof c === 'string' || typeof c === 'number' ? String(c) : '';
            return scalar ? scalar.trim().toLowerCase() : '';
          }
          const looked = lookup.get(String(id));
          return looked || String(id).trim().toLowerCase();
        })
        .filter(Boolean);
    },
    sponsorCategoryLabels(sponsor) {
      const keys = this.sponsorCategoryKeys(sponsor);
      if (!keys.length) return '';
      const opts = this.sponsorCategoryOptions || [];
      return keys
        .map((k) => {
          const found = opts.find((o) => o.key === k);
          return found ? found.label : k;
        })
        .join(' · ');
    },
    onClose() {
      this.$emit('close');
    },
    openSponsor(sponsor) {
      // Close this list modal first so it doesn't linger behind the
      // info modal (and over the sponsor page if the user then taps
      // "Visit Sponsor Page"). Open the info modal on the next tick.
      this.onClose();
      this.$nextTick(() => {
        document.body.classList.add('overflow-hidden');
        this.$modal.show(
          SponsorInfoModal,
          { sponsor },
          {
            adaptive: true,
            minWidth: 320,
            maxWidth: 600,
            width: '90%',
            height: 'auto'
          }
        );
      });
    }
  }
};
</script>

<style scoped lang="scss">
.slm {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 18px 16px;
  display: flex;
  flex-direction: column;
  max-height: 85vh;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }

  &__title-block {
    flex: 1;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 2px;
  }

  &__sub {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  &__close {
    background: transparent;
    border: none;
    color: #6b7280;
    font-size: 18px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }
  }

  &__filters {
    display: flex;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 12px;

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 8px;
    }
  }

  &__search-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 13px;
  }

  &__search {
    width: 100%;
    padding: 10px 14px 10px 36px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    font-size: 14px;
    background: #f9fafb;
    outline: none;
    color: #111827;
    font-family: inherit;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      border-color: #d1d5db;
      background: #ffffff;
    }
  }

  &__category-wrap {
    position: relative;
    flex: 0 0 auto;
    min-width: 160px;

    @media (max-width: 480px) {
      min-width: 0;
    }
  }

  &__category {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 100%;
    padding: 10px 36px 10px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    font-size: 14px;
    font-weight: 500;
    background: #ffffff;
    color: #111827;
    cursor: pointer;
    outline: none;
    font-family: inherit;
    line-height: 1.3;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
      border-color: #d1d5db;
    }
    &:focus {
      border-color: #d1d5db;
    }
  }

  &__category-chevron {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6b7280;
    font-size: 11px;
  }

  &__list {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -6px;
    padding: 0 6px 4px;
  }
}

.slm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover,
  &:focus-visible {
    background: #f3f4f6;
    outline: none;
  }

  & + & {
    border-top: 1px solid #f1f2f4;
  }

  &__logo-wrap {
    width: 56px;
    height: 44px;
    border-radius: 8px;
    background: #f9fafb;
    border: 1px solid #f1f2f4;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__logo-fallback {
    color: #9ca3af;
    font-size: 18px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__category {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.35;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    color: #d1d5db;
    font-size: 12px;
    flex-shrink: 0;
  }
}

.slm-empty {
  text-align: center;
  padding: 32px 12px;
  color: #6b7280;

  p {
    margin: 0;
    font-size: 13px;
  }
}
</style>
